import React, { useState, useEffect } from "react";
import { useSearchParams, useLocation } from 'react-router-dom';
import { AuditTable } from "@/components/audit-centre/AuditTable";
import { AuditAccordion } from "@/components/audit-centre/AuditAccordion";
import { StandaloneNonConformityTable } from "@/components/audit-centre/StandaloneNonConformityTable";
import { StandaloneIncidentTable } from "@/components/audit-centre/StandaloneIncidentTable";
import { StandaloneCorrectiveActionsTable } from "@/components/audit-centre/StandaloneCorrectiveActionsTable";
import { AuditCreateDialog } from "@/components/audit-centre/AuditCreateDialog";
import { AuditEditDialog } from "@/components/audit-centre/AuditEditDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Timestamp as FirebaseTimestamp } from 'firebase/firestore';
import { AuditService } from "./auditService";
import { useActiveTenancy } from "./useActiveTenancy";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useTenancy } from "@/contexts/TenancyContext";
// Remove useToast import as we'll use alert for now

// --- Mock Types ---
export type Audit = {
    id: string;
    type: string;
    scope: string;
    owners: string[];
    auditor: string;
    findings: Finding[];
    incidents: Incident[];
    controls?: string[];
};

export type FindingOrigin = 'audit' | 'standalone';

export type Finding = {
    id: string;
    type: string; // Configurable finding type from menu settings
    status: "Open" | "Closed";
    summary: string;
    owner: string;
    correctiveActions: CorrectiveAction[];
    auditId?: string; // For findings linked to an audit
    origin: FindingOrigin; // Tracks where the finding was created
    createdAt?: Date;
    updatedAt?: Date;
    comments?: string;
    // DED-1141: Date closed field shown when status is Closed
    dateClosed?: Date | string;
    // DED-973: Linked documents for findings
    linkedDocuments?: string[];
};

export type CorrectiveAction = {
    id: string;
    status: "Open" | "Closed";
    summary: string;
    owner: string;
    dueDate?: Date | string | FirebaseTimestamp; // Due date for the corrective action
    findingId?: string; // For corrective actions linked to a finding
    auditId?: string; // For corrective actions linked to an audit
    createdAt?: Date;
    updatedAt?: Date;
    // DED-973: Linked documents for corrective actions
    linkedDocuments?: string[];
};

export type Incident = {
    id: string;
    status: "Open" | "Closed";
    summary: string;
    owner: string;
    auditId?: string; // For incidents linked to an audit
    createdAt?: Date;
    updatedAt?: Date;
    // DED-1141: Date closed field shown when status is Closed
    dateClosed?: Date | string;
    // DED-1141: Additional fields from rework
    dateOfIncident?: Date | string; // Date when the incident occurred
    impact?: string; // Free text field for impact description
    supportingDocuments?: string[]; // DED-1141: References to documents in document library
    closeComment?: string; // Comment when closing the incident
    // DED-1339: Controls for MCM integration
    controls?: string[];
};

// Map URL paths to tab values
const pathToTab: Record<string, string> = {
    '/auditcentre/audits': 'audit',
    '/auditcentre/findings': 'nonconformity',
    '/auditcentre/incidents': 'incidents',
    '/auditcentre/corrective-actions': 'corrective-actions',
};

// --- Firestore Data State ---
export default function AuditCentrePage() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    
    // Determine if we're on a direct route (hide tab bar) or main route (show tab bar)
    const isDirectRoute = Object.keys(pathToTab).includes(location.pathname);
    
    // Get initial tab from URL path or default to 'audit'
    const getTabFromPath = () => {
        return pathToTab[location.pathname] || 'audit';
    };
    
    const [tab, setTab] = React.useState(getTabFromPath);
    
    // Update tab when location changes
    React.useEffect(() => {
        const newTab = getTabFromPath();
        if (newTab !== tab) {
            setTab(newTab);
        }
    }, [location.pathname]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
    const [audits, setAudits] = React.useState<Audit[]>([]);
    const [standaloneNonConformities, setStandaloneNonConformities] = React.useState<Finding[]>([]);
    const [standaloneIncidents, setStandaloneIncidents] = React.useState<Incident[]>([]);
    const [standaloneCorrectiveActions, setStandaloneCorrectiveActions] = React.useState<CorrectiveAction[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    // --- Edit dialog state ---
    const [editDialogOpen, setEditDialogOpen] = React.useState(false);
    const [selectedAudit, setSelectedAudit] = React.useState<Audit | null>(null);
    // --- Available owners ---
    const { userIdToName } = useTenancy();
    const availableOwners = userIdToName ? Object.keys(userIdToName) : [];

    const activeTenancy = useActiveTenancy();
    const auditService = React.useMemo(() => {
        return activeTenancy ? new AuditService({ activeTenancy }) : null;
    }, [activeTenancy]);

    // Load data from Firestore
    const loadData = async () => {
        if (!auditService) return;

        try {
            setIsLoading(true);
            const [auditsData, nonConformitiesData, incidentsData, correctiveActionsData] = await Promise.all([
                auditService.listAudits(),
                auditService.listStandaloneFindings(),
                auditService.listStandaloneIncidents(),
                auditService.listStandaloneCorrectiveActions()
            ]);

            // DED-1448: Load findings from each audit's subcollection
            // This ensures findings created in audit view appear in the findings tab
            const auditsWithFindings = await Promise.all(
                auditsData.map(async (audit) => {
                    const auditFindings = await auditService.listFindings(audit.id);
                    return {
                        ...audit,
                        findings: auditFindings.map(f => ({ ...f, auditId: audit.id, origin: 'audit' as const }))
                    };
                })
            );

            setAudits(auditsWithFindings);
            setStandaloneNonConformities(nonConformitiesData);
            setStandaloneIncidents(incidentsData);
            setStandaloneCorrectiveActions(correctiveActionsData);
        } catch (err) {
            console.error("Error loading data:", err);
            setError("Failed to load audit data. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };



    // Initial load
    React.useEffect(() => {
        loadData();
    }, [auditService]);

    // Read tab from URL params
    React.useEffect(() => {
        const t = searchParams.get('tab');
        const validTabs = new Set(['audit', 'nonconformity', 'incidents', 'corrective-actions']);
        if (t && validTabs.has(t) && t !== tab) {
            setTab(t);
        }
    }, [searchParams]);

    // Deep-link: open audit edit dialog when tab=audit&id present
    React.useEffect(() => {
        const id = searchParams.get('id');
        if (!id || tab !== 'audit' || audits.length === 0) return;
        const match = audits.find(a => a.id === id);
        if (match) {
            setSelectedAudit(match);
            setEditDialogOpen(true);
        }
    }, [searchParams, tab, audits]);

    // Handle audit edit (open dialog)
    const handleEditAudit = (audit: Audit) => {
        setSelectedAudit(audit);
        setEditDialogOpen(true);
    };

    // Save handler for edit dialog
    const handleEditDialogSave = async (updates: Omit<Audit, 'id' | 'findings' | 'incidents'> & { controls?: string[] }) => {
        if (!auditService || !selectedAudit) return;
        setIsLoading(true);
        try {
            await auditService.updateAudit(selectedAudit.id, updates);
            await loadData();
            setEditDialogOpen(false);
            setSelectedAudit(null);
        } catch (err) {
            setError("Failed to update audit");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle audit creation
    const handleCreateAudit = async (auditData: Omit<Audit, 'id' | 'findings' | 'incidents'> & {
        findings: Array<Omit<Finding, 'id'>>;
        incidents: Array<Omit<Incident, 'id'>>;
    }): Promise<string | void> => {
        if (!auditService) return;

        try {
            setIsLoading(true);
            // First create the audit
            const auditId = await auditService.createAudit({
                type: auditData.type,
                scope: auditData.scope,
                owners: auditData.owners,
                auditor: auditData.auditor,
                controls: auditData.controls || [], // Include controls
                findings: [], // Will be populated separately
                incidents: [], // Will be populated separately
            });

            // Create findings
            const findingsPromises = auditData.findings.map((finding) => {
                return auditService.createFinding(auditId, {
                    ...finding,
                    auditId: auditId, // Ensure auditId is set
                });
            });

            // Create incidents
            const incidentsPromises = auditData.incidents.map((incident) => {
                return auditService.createIncident({
                    ...incident,
                    auditId: auditId, // Ensure auditId is set
                });
            });

            await Promise.all([...findingsPromises, ...incidentsPromises]);

            // Refresh data
            await loadData();

            // Show success message
            // alert("Audit created successfully!");
            toast.success("Audit created successfully!");
            return auditId;
        } catch (error) {
            console.error("Error creating audit:", error);
            setError("Failed to create audit. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }

    // Handlers for Firestore actions
    async function handleCreateFinding(newFinding: {
        summary: string;
        owner: string;
        status: "Open" | "Closed";
        type: "Minor NC" | "Major NC" | "OFI";
        auditId?: string;
        origin?: 'audit' | 'standalone';
    }) {
        if (!auditService) return;
        try {
            setIsLoading(true);
            // Create a new finding with required fields from the Finding type
            const finding: Omit<Finding, 'id'> = {
                ...newFinding,
                origin: newFinding.origin || 'standalone',
                correctiveActions: [], // Initialize with empty array
                createdAt: new Date(),
                updatedAt: new Date()
            };
            const id = await auditService.createStandaloneFinding(finding);
            setStandaloneNonConformities(prev => [...prev, { ...finding, id } as Finding]);
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to create finding');
        } finally {
            setIsLoading(false);
        }
    }

    const handleUpdateFinding = async (findingId: string, updates: Partial<Finding>): Promise<boolean> => {
        if (!auditService) return false;

        try {
            // Find the finding in our local state
            const finding = standaloneNonConformities.find(f => f.id === findingId);
            if (!finding) {
                throw new Error('Finding not found');
            }

            // Preserve the original origin in updates
            const updateData = {
                ...updates,
                origin: finding.origin, // Always preserve the original origin
                updatedAt: new Date()
            };

            // Handle based on the finding's origin and current audit association
            if (finding.origin === 'audit') {
                // For audit-native findings, always update in the audit
                await auditService.updateFinding(finding.auditId!, findingId, updateData);
            } else if (finding.auditId) {
                // For standalone findings linked to an audit, update in the standalone collection
                await auditService.updateStandaloneFinding(findingId, updateData);
            } else {
                // For standalone findings not linked to an audit
                await auditService.updateStandaloneFinding(findingId, updateData);
            }

            // Refresh the data to ensure consistency
            await loadData();
            return true;
        } catch (err) {
            console.error("Failed to update finding:", err);
            setError(`Failed to update finding: ${err.message}`);
            return false;
        }
    };
    async function handleDeleteFinding(id: string) {
        if (!auditService) return;
        try {
            setIsLoading(true);
            await auditService.deleteStandaloneFinding(id);
            setStandaloneNonConformities(prev => prev.filter(f => f.id !== id));
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to delete finding');
        } finally {
            setIsLoading(false);
        }
    }

    // Delete handler for audit
    const handleDeleteAudit = async (auditId: string) => {
        if (!auditService) return;
        setIsLoading(true);
        try {
            await auditService.deleteAudit(auditId);
            await loadData();
            toast.success('Audit deleted successfully');
        } catch (err) {
            setError('Failed to delete audit');
        } finally {
            setIsLoading(false);
        }
    };

    let content = null;
    if (isLoading) content = <div className="p-8">Loading...</div>;
    else if (error) content = <div className="p-8 text-red-500">{error}</div>;
    else content = (
        <div className="page-container mx-auto p-4 space-y-4"> ... </div>
    );

    return (
        <>
            {selectedAudit && (
                <AuditEditDialog
                    open={editDialogOpen}
                    onOpenChange={setEditDialogOpen}
                    audit={selectedAudit}
                    onSave={handleEditDialogSave}
                    isSubmitting={isLoading}
                    availableOwners={availableOwners}
                />
            )}            
            <div
                className="page-container mx-auto p-4 space-y-4 overflow-y-auto max-h-screen sm:max-h-none sm:overflow-visible"
                style={{ WebkitOverflowScrolling: 'touch' }}
            >
                <div className="glass-card">
                    <div className="flex items-center justify-between py-4">
                        <h1 className="text-2xl font-bold my-0">
                            {/* Show specific title based on current tab when on direct route */}
                            {isDirectRoute ? (
                                tab === 'audit' ? 'Audits' :
                                tab === 'nonconformity' ? 'Findings' :
                                tab === 'incidents' ? 'Incidents' :
                                tab === 'corrective-actions' ? 'Corrective Actions' :
                                'Audit Centre'
                            ) : 'Audit Centre'}
                        </h1>
                        {/* Only show tab bar when NOT on a direct route */}
                        {!isDirectRoute && (
                            <div className="flex-1 px-4">
                                <Tabs value={tab} onValueChange={setTab} className="m-0">
                                    <TabsList className="mx-auto">
                                        <TabsTrigger value="audit" className={`transition-all data-[state=active]:bg-primary data-[state=active]:text-white`}>Audit View</TabsTrigger>
                                        <TabsTrigger value="nonconformity" className={`transition-all data-[state=active]:bg-primary data-[state=active]:text-white`}>Findings</TabsTrigger>
                                        <TabsTrigger value="incidents" className={`transition-all data-[state=active]:bg-primary data-[state=active]:text-white`}>Incidents</TabsTrigger>
                                        <TabsTrigger value="corrective-actions" className={`transition-all data-[state=active]:bg-primary data-[state=active]:text-white`}>Corrective Actions</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>
                        )}
                        {!isDirectRoute && <div className="w-24"></div>} {/* Spacer for balance - only when tabs shown */}
                    </div>
                    <AuditCreateDialog
                        open={isCreateDialogOpen}
                        onOpenChange={setIsCreateDialogOpen}
                        onCreate={handleCreateAudit}
                        auditService={auditService!}
                    />
                    {/* <Button onClick={() => setIsCreateDialogOpen(true)}>Create Audit</Button> */}
                    {tab === "audit" && (
                        <AuditTable
                            audits={audits}
                            isCreateDialogOpen={isCreateDialogOpen}
                            setIsCreateDialogOpen={setIsCreateDialogOpen}
                            handleCreateAudit={handleCreateAudit}
                            auditService={auditService}
                            onEdit={handleEditAudit}
                            onDelete={handleDeleteAudit}
                        />
                    )}
                    {tab === "nonconformity" && (
                        <StandaloneNonConformityTable
                            findings={[
                                ...standaloneNonConformities.filter(f => f.type === "Minor NC" || f.type === "Major NC" || f.type === "OFI"),
                                ...audits.flatMap(audit =>
                                    (audit.findings || [])
                                        .filter(f => f.type === "Minor NC" || f.type === "Major NC" || f.type === "OFI")
                                        .map(f => ({ ...f, auditId: audit.id }))
                                )
                            ]}
                            audits={audits}
                            deepLinkedFindingId={searchParams.get('id') || undefined}
                            onLink={async (findingId, auditId) => {
                                if (!auditService) return;
                                try {
                                    setIsLoading(true);
                                    await auditService.updateStandaloneFinding(findingId, { auditId });
                                    await loadData(); // Refresh data
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to link finding');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onUnlink={async (findingId) => {
                                if (!auditService) return;
                                try {
                                    setIsLoading(true);
                                    await auditService.updateStandaloneFinding(findingId, { auditId: null });
                                    await loadData(); // Refresh data
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to unlink finding');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onEdit={async (findingId) => {
                                if (!auditService) return;
                                try {
                                    setIsLoading(true);
                                    await auditService.updateStandaloneFinding(findingId, { auditId: null });
                                    await loadData(); // Refresh data
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to edit finding');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onSave={async (finding: any) => {
                                console.log('[AuditCentrePage] onSave', finding);
                                try {
                                    setIsLoading(true);
                                    await handleUpdateFinding(String(finding.id), finding);
                                    await loadData(); // Refresh data
                                    return true;
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to save finding');
                                    return false;
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onDelete={async (findingId) => {
                                if (!auditService) return;
                                try {
                                    setIsLoading(true);
                                    await auditService.deleteStandaloneFinding(findingId);
                                    await loadData(); // Refresh data
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to delete finding');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onCreate={handleCreateFinding}
                            onActionCreated={async (action) => {
                                // Update local state with the new corrective action
                                if (action.findingId) {
                                    setStandaloneNonConformities(prev =>
                                        prev.map(finding =>
                                            finding.id === action.findingId
                                                ? {
                                                    ...finding,
                                                    correctiveActions: [...(finding.correctiveActions || []), action]
                                                }
                                                : finding
                                        )
                                    );
                                }
                            }}
                            onActionUpdated={async (actionId, updates) => {
                                // Update local state with the updated corrective action
                                setStandaloneNonConformities(prev =>
                                    prev.map(finding => ({
                                        ...finding,
                                        correctiveActions: (finding.correctiveActions || []).map(ca =>
                                            ca.id === actionId ? { ...ca, ...updates } : ca
                                        )
                                    }))
                                );
                            }}
                            onActionDeleted={async (actionId) => {
                                // Update local state by removing the deleted corrective action
                                setStandaloneNonConformities(prev =>
                                    prev.map(finding => ({
                                        ...finding,
                                        correctiveActions: (finding.correctiveActions || []).filter(ca => ca.id !== actionId)
                                    }))
                                );
                            }}
                            auditService={auditService!}
                        />
                    )}
                    {tab === "incidents" && (
                        <StandaloneIncidentTable
                            incidents={standaloneIncidents}
                            audits={audits}
                            deepLinkedIncidentId={searchParams.get('id') || undefined}
                            onIncidentCreated={async (newIncident) => {
                                // Add the new incident to the standalone incidents list
                                setStandaloneIncidents(prev => [...prev, newIncident]);

                                // If the incident is linked to an audit, update the audit's incidents list
                                if (newIncident.auditId) {
                                    setAudits(prevAudits =>
                                        prevAudits.map(audit =>
                                            audit.id === newIncident.auditId
                                                ? {
                                                    ...audit,
                                                    incidents: [...(audit.incidents || []), newIncident]
                                                }
                                                : audit
                                        )
                                    );
                                }
                                return true;
                            }}
                            onDelete={async (incidentId) => {
                                if (!auditService) return;
                                try {
                                    // Optimistically remove from local standalone incidents to avoid flicker/reinsertion
                                    setStandaloneIncidents(prev => prev.filter(i => i.id !== incidentId));
                                    setIsLoading(true);
                                    console.log("Deleting incident", (incidentId as any)?.id || incidentId);
                                    await auditService.deleteIncident((incidentId as any)?.id || incidentId);
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to delete incident');
                                } finally {
                                    setIsLoading(false);
                                    // Schedule a delayed background refresh to let Firestore propagate the delete
                                    setTimeout(() => {
                                        loadData().catch((e) => console.error('Background reload after delete failed', e));
                                    }, 750);
                                }
                            }}
                            onIncidentUpdated={async (incidentId, updates) => {
                                if (!auditService) return false;
                                try {
                                    setIsLoading(true);
                                    await auditService.updateIncident(incidentId, updates);
                                    await loadData();
                                    return true;
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to update incident');
                                    return false;
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onLink={async (incidentId, auditId) => {
                                if (!auditService) return false;
                                try {
                                    setIsLoading(true);
                                    await auditService.linkIncidentToAudit(incidentId, auditId);
                                    await loadData(); // Refresh data
                                    return true;
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to link incident');
                                    return false;
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onUnlink={async (incidentId) => {
                                if (!auditService) return false;
                                try {
                                    setIsLoading(true);
                                    await auditService.unlinkIncidentFromAudit(incidentId);
                                    await loadData(); // Refresh data
                                    return true;
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to unlink incident');
                                    return false;
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                        />
                    )}
                    {tab === "corrective-actions" && (
                        <StandaloneCorrectiveActionsTable
                            correctiveActions={standaloneCorrectiveActions}
                            audits={audits}
                            findings={[
                                ...standaloneNonConformities,
                                ...audits.flatMap(audit => audit.findings || [])
                            ]}
                            deepLinkedActionId={searchParams.get('id') || undefined}
                            onLink={async (actionId, findingId) => {
                                if (!auditService) return;
                                try {
                                    setIsLoading(true);
                                    await auditService.linkCorrectiveActionToFinding(actionId, findingId);
                                    await loadData();
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to link corrective action');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onUnlink={async (actionId) => {
                                if (!auditService) return;
                                try {
                                    setIsLoading(true);
                                    await auditService.unlinkCorrectiveAction(actionId);
                                    await loadData();
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to unlink corrective action');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onCreate={async (action, findingId) => {
                                if (!auditService) return;
                                try {
                                    setIsLoading(true);
                                    const newAction = await auditService.createStandaloneCorrectiveAction({
                                        ...action,
                                        ...(findingId ? { findingId } : {})
                                    });
                                    setStandaloneCorrectiveActions(prev => [...prev, newAction]);
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to create corrective action');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onUpdate={async (id, updates) => {
                                if (!auditService) return;
                                try {
                                    setIsLoading(true);
                                    await auditService.updateStandaloneCorrectiveAction(id, updates);
                                    await loadData();
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to update corrective action');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            onDelete={async (id) => {
                                if (!auditService) return;
                                try {
                                    setIsLoading(true);
                                    await auditService.deleteStandaloneCorrectiveAction(id);
                                    setStandaloneCorrectiveActions(prev => prev.filter(a => a.id !== id));
                                } catch (error) {
                                    setError(error instanceof Error ? error.message : 'Failed to delete corrective action');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        </>
    );
}